import { useId, useState } from 'react';
import { useStrings } from '../../state/useLocale';

/**
 * A two-line postmortem, offered on the clear when the attempt took more than
 * one check: what went wrong first, and what changed it.
 *
 * Blameless and private, the way the practice is in aviation's safety reports
 * and in engineering postmortems: the note is about the work, it is kept only
 * in this browser for this account, nothing reads it, and nothing grades it.
 * Writing it is the point; saying it in your own words after the fact is what
 * turns a failed check into something you keep (docs/learning-practice.md).
 */
const KEY = 'engineeros.postmortem.v1';
const keyFor = (userId: string | null, mission: string) => `${KEY}:${userId ?? 'guest'}:${mission}`;

interface Note { wrong: string; changed: string }

function read(userId: string | null, mission: string): Note {
  try {
    const raw: unknown = JSON.parse(globalThis.localStorage?.getItem(keyFor(userId, mission)) ?? '{}');
    const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return { wrong: typeof r.wrong === 'string' ? r.wrong : '', changed: typeof r.changed === 'string' ? r.changed : '' };
  } catch { return { wrong: '', changed: '' }; }
}

function write(userId: string | null, mission: string, note: Note): boolean {
  try {
    globalThis.localStorage?.setItem(keyFor(userId, mission), JSON.stringify(note));
    return Boolean(globalThis.localStorage);
  } catch { return false; }
}

export function Postmortem({ mission, userId }: { mission: string; userId: string | null }) {
  const s = useStrings().progress.debrief.postmortem;
  const id = useId();
  const [note, setNote] = useState(() => read(userId, mission));
  const [saved, setSaved] = useState<boolean | null>(null);
  const edit = (field: keyof Note) => (event: { currentTarget: { value: string } }) => {
    const next = { ...note, [field]: event.currentTarget.value };
    setNote(next);
    setSaved(write(userId, mission, next));
  };
  return <details className="clear-postmortem">
    <summary>{s.open}</summary>
    <p className="note">{s.note}</p>
    <label htmlFor={`${id}-wrong`}>{s.wrong}</label>
    <textarea id={`${id}-wrong`} rows={2} maxLength={400} value={note.wrong} onChange={edit('wrong')} />
    <label htmlFor={`${id}-changed`}>{s.changed}</label>
    <textarea id={`${id}-changed`} rows={2} maxLength={400} value={note.changed} onChange={edit('changed')} />
    {saved !== null && <p className="note" role="status">{saved ? s.saved : s.unsaved}</p>}
  </details>;
}
